import * as THREE from 'three';
import {
  bevelBox, extrudeShape, filletShape, chamferCyl, chamferCone,
  stippleField, picatinnyRail,
} from './Bevel.js';
import { place } from './Builder.js';
import {
  sideProfile, frontProfile, cylZ, coneZ, tubeZ, decal, screwY, screwX, crossPin, knurl,
  stanagMag, pistolGrip, triggerGroup,
} from './Kit.js';
import { boxTubeY } from './Rifle.js';

/* ================================================================== *
 * SMG — MP5A3 class. 9x19, 680 mm with the stock retracted.
 * ================================================================== */

export function buildSMG(B, rng) {
  B.bone('root');
  B.bone('receiver', 'root');
  B.bone('bolt', 'receiver', [0, 0, -0.060]);
  B.bone('charging', 'receiver', [-0.0168, 0.0128, -0.280]);
  B.bone('mag', 'receiver', [0, -0.0180, -0.1520]);
  B.bone('trigger', 'receiver', [0, -0.0300, -0.0480]);
  B.bone('selector', 'receiver', [-0.0182, -0.0210, -0.0330]);
  B.bone('magrelease', 'receiver', [0, -0.0230, -0.1250]);
  B.bone('stock', 'receiver', [0, -0.0040, 0.0900]);
  B.bone('sights', 'receiver');

  const bev = 0.0007;
  const SIGHT = 0.0332;

  /* ---- stamped receiver tube ---- */
  const rec = [
    [-0.0178, -0.0058], [-0.0148, -0.0142], [0.0148, -0.0142], [0.0178, -0.0058],
    [0.0178, 0.0092], [0.0122, 0.0172], [-0.0122, 0.0172], [-0.0178, 0.0092],
  ];
  const recPort = [
    [-0.0178, -0.0058], [-0.0148, -0.0142], [0.0148, -0.0142], [0.0178, -0.0058],
    [0.0178, -0.0010], [0.0098, -0.0010], [0.0098, 0.0140], [0.0160, 0.0140],
    [0.0122, 0.0172], [-0.0122, 0.0172], [-0.0178, 0.0092],
  ];
  const segAt = (pts, z0, z1) => frontProfile(pts, 0.0026, z1 - z0, bev, 4).translate(0, 0, (z0 + z1) / 2);
  B.add([
    segAt(rec, -0.2360, -0.0950),
    segAt(recPort, -0.0950, -0.0480),
    segAt(rec, -0.0480, 0.0120),
  ], 'metal', 'receiver', null, { edgeWear: 0.45 });

  // rear end cap + sling loop
  B.add(frontProfile(rec, 0.0026, 0.0075, bev, 4).translate(0, 0, 0.0155), 'metal', 'receiver',
    null, { edgeWear: 0.8 });
  B.add(tubeZ(0.0062, 0.0036, 0.0030, 0.0004, 14), 'bare', 'receiver',
    place(-0.0150, 0.0140, 0.0180, 0, Math.PI / 2, 0), { edgeWear: 0.9, wear: 0.4 });

  // ejection port lip and shell deflector
  B.add(sideProfile([
    [-0.0955, -0.0035], [-0.0475, -0.0035], [-0.0475, -0.0002], [-0.0955, -0.0002],
  ], 0.0007, 0.0075, 0.0005), 'metal', 'receiver', place(0.0148, 0, 0), { edgeWear: 0.9 });

  /* ---- cocking-handle tube on the upper left ---- */
  B.add(tubeZ(0.0098, 0.0072, 0.1080, 0.0008, 20), 'metal', 'receiver',
    place(-0.0168, 0.0128, -0.2840), { edgeWear: 0.6 });
  B.add(cylZ(0.0098, 0.0140, 0.0008, 20), 'metal', 'receiver',
    place(-0.0168, 0.0128, -0.2320), { edgeWear: 0.8 });
  // the handle itself, plus the HK slap notch
  B.add([
    bevelBox(0.0140, 0.0092, 0.0300, 0.0007).translate(-0.0090, 0, 0),
    bevelBox(0.0072, 0.0170, 0.0175, 0.0007).translate(-0.0150, -0.0010, 0),
  ], 'metal', 'charging', place(-0.0168, 0.0128, -0.2800), { edgeWear: 0.95 });
  for (let i = 0; i < 4; i++) {
    B.add(bevelBox(0.0012, 0.0150, 0.0016, 0.0003), 'metal', 'charging',
      place(-0.0190, 0.0128, -0.2870 + i * 0.0028), { wear: 0.6 });
  }
  // bolt seen through the port
  B.add(cylZ(0.0140, 0.0980, 0.0009, 20), 'bare', 'bolt', place(0, 0, -0.0600), { wear: 0.55 });
  B.add(cylZ(0.0102, 0.0090, 0.0008, 18), 'bare', 'bolt', place(0, 0, -0.1080), { wear: 0.85 });

  /* ---- handguard ---- */
  const hgZ0 = -0.2360, hgZ1 = -0.3400, hgC = (hgZ0 + hgZ1) / 2;
  B.add(tubeZ(0.0222, 0.0176, hgZ0 - hgZ1, 0.0011, 22), 'polymer', 'receiver',
    place(0, -0.0012, hgC), { edgeWear: 0.35 });
  // cooling slots as recessed strips
  for (let i = 0; i < 8; i++) {
    const a = -0.9 + (i / 7) * 1.8;
    for (const sx of [-1, 1]) {
      B.add(bevelBox(0.0032, 0.0022, 0.0620, 0.0005), 'polymer', 'receiver',
        place(sx * Math.cos(a) * 0.0224, -0.0012 + Math.sin(a) * 0.0224, hgC,
          0, 0, sx * a - Math.PI / 2), { edgeWear: 0.5 });
    }
  }
  B.add(tubeZ(0.0234, 0.0210, 0.0090, 0.0008, 22), 'polymer', 'receiver',
    place(0, -0.0012, hgZ0 - 0.0040), { edgeWear: 0.6 });
  B.add(tubeZ(0.0230, 0.0200, 0.0080, 0.0008, 22), 'polymer', 'receiver',
    place(0, -0.0012, hgZ1 + 0.0040), { edgeWear: 0.7 });

  /* ---- barrel + tri-lug muzzle ---- */
  B.add([
    tubeZ(0.0074, 0.0046, 0.1300, 0.0006, 18).translate(0, 0, -0.2960),
    cylZ(0.0106, 0.0180, 0.0008, 18).translate(0, 0, -0.2450),
  ], 'steel', 'receiver', null, { wear: 0.2 });
  B.add(tubeZ(0.0092, 0.0046, 0.0230, 0.0007, 18), 'steel', 'receiver',
    place(0, 0, -0.3520), { edgeWear: 0.7, wear: 0.35 });
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.4;
    B.add(bevelBox(0.0036, 0.0044, 0.0130, 0.0005), 'steel', 'receiver',
      place(Math.cos(a) * 0.0100, Math.sin(a) * 0.0100, -0.3560, 0, 0, a - Math.PI / 2),
      { edgeWear: 0.8 });
  }

  /* ---- sights: hooded front post, rotary rear drum ---- */
  const fz = -0.3320;
  B.add(sideProfile([
    [fz - 0.010, 0.0100], [fz + 0.010, 0.0100], [fz + 0.010, 0.0210], [fz - 0.010, 0.0210],
  ], 0.0020, 0.0170, 0.0006), 'steel', 'sights', null, { edgeWear: 0.6 });
  B.add(tubeZ(0.0138, 0.0104, 0.0180, 0.0007, 20), 'steel', 'sights',
    place(0, SIGHT, fz), { edgeWear: 0.75 });
  B.add(bevelBox(0.0022, 0.0110, 0.0060, 0.0004), 'steel', 'sights',
    place(0, SIGHT - 0.0050, fz), { edgeWear: 1.0 });

  const rz = -0.0180;
  B.add(sideProfile([
    [rz - 0.014, 0.0110], [rz + 0.014, 0.0110], [rz + 0.014, 0.0215], [rz - 0.014, 0.0215],
  ], 0.0022, 0.0180, 0.0006), 'steel', 'sights', null, { edgeWear: 0.6 });
  B.add(chamferCyl(0.0118, 0.0175, 0.0008, 20).rotateX(Math.PI / 2), 'steel', 'sights',
    place(0, SIGHT, rz), { edgeWear: 0.7 });
  B.add(knurl(0.0118, 0.0175, 18, 0.0011), 'steel', 'sights',
    place(0, SIGHT, rz, Math.PI / 2, 0, 0), { wear: 0.5 });
  B.add(tubeZ(0.0042, 0.0020, 0.0070, 0.0004, 14), 'steel', 'sights',
    place(0, SIGHT, rz - 0.0090), { edgeWear: 0.9, wear: 0.6 });

  // short optics rail behind the drum
  const rail = picatinnyRail(0.0620);
  B.add(rail.parts, 'metal', 'receiver', place(0, 0.0172, -0.1600), { edgeWear: 0.8 });

  /* ---- trigger group housing (polymer) ---- */
  B.add(sideProfile([
    [-0.1180, -0.0140], [-0.1180, -0.0330], [-0.0180, -0.0330], [0.0180, -0.0300],
    [0.0180, -0.0140],
  ], 0.0034, 0.0356, 0.0011, 4), 'polymer', 'receiver', null, { edgeWear: 0.35 });
  triggerGroup(B, 'receiver', 'trigger', { z: -0.0480, y: -0.0330, width: 0.0120, mat: 'polymer' });
  B.add(decal('hk_selector', 0.0230, 'left', -0.0184, -0.0215, -0.0300),
    'marks', 'receiver', null, { keepUV: true });
  // selector lever
  for (const s of [-1, 1]) {
    B.add([
      chamferCyl(0.0070, 0.0042, 0.0005, 16).rotateZ(Math.PI / 2),
      bevelBox(0.0038, 0.0072, 0.0220, 0.0006).translate(0, -0.0010, 0.0095),
    ], 'metal', 'selector', place(s * 0.0184, -0.0210, -0.0330), { edgeWear: 0.9 });
  }

  const grip = pistolGrip(B, 'receiver', rng, { tilt: 0.38, length: 0.0980, y: -0.0330, z: -0.0180, mat: 'polymer' });

  /* ---- magazine well + curved 9 mm magazine ---- */
  B.add(boxTubeY(0.0350, 0.0400, 0.0046, 0.0420, 0.0010), 'polymer', 'receiver',
    place(0, -0.0140, -0.1520), { edgeWear: 0.5 });
  B.add(bevelBox(0.0180, 0.0150, 0.0110, 0.0008), 'polymer', 'magrelease',
    place(0, -0.0230, -0.1250), { edgeWear: 0.7 });
  stanagMag(B, 'mag', {
    mat: 'metal', length: 0.2280, width: 0.0270, depth: 0.0290, curve: 0.30,
    at: place(0, -0.0180, -0.1520),
  });

  /* ---- retractable stock ---- */
  for (const sx of [-1, 1]) {
    B.add(cylZ(0.0062, 0.1750, 0.0006, 14), 'metal', 'stock',
      place(sx * 0.0148, -0.0020, 0.0960), { edgeWear: 0.55 });
  }
  B.add(sideProfile([
    [0.1720, -0.0200], [0.1930, -0.0140], [0.1930, 0.0180], [0.1720, 0.0210],
  ], 0.0035, 0.0400, 0.0011, 3), 'rubber', 'stock', place(0, 0, 0), { edgeWear: 0.3 });
  B.add(bevelBox(0.0400, 0.0300, 0.0110, 0.0010), 'metal', 'stock',
    place(0, 0.0000, 0.1640), { edgeWear: 0.6 });

  B.add(decal('roll_smg', 0.0520, 'left', -0.0179, -0.0035, -0.1350),
    'marks', 'receiver', null, { keepUV: true });
  B.add(decal('serial_small', 0.0250, 'right', 0.0179, -0.0090, -0.1350),
    'marks', 'receiver', null, { keepUV: true });

  const gripAxis = new THREE.Vector3(0, -1, 0).transformDirection(grip.matrix).normalize();
  const gripCentre = new THREE.Vector3(0, -0.048, 0).applyMatrix4(grip.matrix);
  return {
    name: 'smg',
    sight: { axis: SIGHT, z: fz },
    ironSight: { axis: SIGHT, z: fz },
    muzzle: new THREE.Vector3(0, 0, -0.3660),
    ejectPort: new THREE.Vector3(0.0220, 0.0060, -0.0700),
    ejectDir: new THREE.Vector3(0.94, 0.30, 0.16).normalize(),
    magBone: 'mag',
    anchors: {
      rightGrip: { point: gripCentre, dir: gripAxis, radius: 0.0178 },
      leftGrip: { point: new THREE.Vector3(0, -0.0012, -0.2840), dir: new THREE.Vector3(0, 0, 1), radius: 0.0248 },
      rightRadial: [0.58, 0.30, 0.76], rightAlong: -0.028,
      leftRadial: [-0.60, -0.80, 0.0], leftAlong: 0.024,
      leftForearm: [-0.30, -0.74, 0.60],
      trigger: new THREE.Vector3(0, -0.0600, -0.0500),
      magGrab: new THREE.Vector3(0, -0.1600, -0.1450),
    },
    grip,
  };
}

/* ================================================================== *
 * Pump shotgun — Remington 870 class, 18.5" barrel.
 * ================================================================== */

export function buildShotgun(B, rng) {
  B.bone('root');
  B.bone('receiver', 'root');
  B.bone('bolt', 'receiver', [0, 0, -0.060]);
  B.bone('pump', 'receiver', [0, -0.0130, -0.3700]);
  B.bone('trigger', 'receiver', [0, -0.0300, -0.0420]);
  B.bone('lifter', 'receiver', [0, -0.0160, -0.0800]);
  B.bone('stock', 'receiver', [0, 0, 0.0200]);
  B.bone('sights', 'receiver');

  const bev = 0.0009;
  const RIB = 0.0170;

  /* ---- milled steel receiver ---- */
  const rec = [
    [-0.0192, -0.0180], [0.0192, -0.0180], [0.0210, -0.0090], [0.0210, 0.0130],
    [0.0140, 0.0208], [-0.0140, 0.0208], [-0.0210, 0.0130], [-0.0210, -0.0090],
  ];
  const recPort = [
    [-0.0192, -0.0180], [0.0192, -0.0180], [0.0210, -0.0090], [0.0210, -0.0035],
    [0.0110, -0.0035], [0.0110, 0.0140], [0.0184, 0.0140],
    [0.0140, 0.0208], [-0.0140, 0.0208], [-0.0210, 0.0130], [-0.0210, -0.0090],
  ];
  const seg = (pts, z0, z1) => frontProfile(pts, 0.0030, z1 - z0, bev, 4).translate(0, 0, (z0 + z1) / 2);
  B.add([
    seg(rec, -0.1960, -0.1050),
    seg(recPort, -0.1050, -0.0430),
    seg(rec, -0.0430, 0.0100),
  ], 'metal', 'receiver', null, { edgeWear: 0.45 });

  // loading port on the underside with the shell lifter inside
  B.add(sideProfile([
    [-0.1000, -0.0195], [-0.0450, -0.0195], [-0.0450, -0.0155], [-0.1000, -0.0155],
  ], 0.0008, 0.0300, 0.0006), 'metal', 'receiver', null, { edgeWear: 0.9 });
  B.add(sideProfile([
    [-0.0980, -0.0165], [-0.0480, -0.0165], [-0.0480, -0.0120], [-0.0980, -0.0135],
  ], 0.0010, 0.0230, 0.0006), 'bare', 'lifter', null, { edgeWear: 0.9, wear: 0.55 });

  // breech bolt seen through the port
  B.add(bevelBox(0.0210, 0.0210, 0.0900, 0.0009), 'bare', 'bolt',
    place(0, 0.0010, -0.0600), { wear: 0.5 });
  B.add(cylZ(0.0110, 0.0110, 0.0008, 18), 'bare', 'bolt',
    place(0, 0.0010, -0.1080), { wear: 0.85 });

  /* ---- barrel with a vent rib, plus the magazine tube ---- */
  B.add(tubeZ(0.0126, 0.0094, 0.4520, 0.0009, 22), 'bare', 'receiver',
    place(0, 0.0000, -0.4210), { wear: 0.18 });
  B.add(cylZ(0.0158, 0.0300, 0.0010, 22), 'bare', 'receiver',
    place(0, 0, -0.2060), { edgeWear: 0.6, wear: 0.3 });
  // vent rib
  B.add(bevelBox(0.0088, 0.0032, 0.4300, 0.0006), 'bare', 'receiver',
    place(0, RIB - 0.0016, -0.4300), { edgeWear: 0.7 });
  for (let i = 0; i < 16; i++) {
    B.add(bevelBox(0.0056, 0.0060, 0.0060, 0.0006), 'steel', 'receiver',
      place(0, RIB - 0.0058, -0.2250 - i * 0.0260), { edgeWear: 0.5 });
  }
  // brass bead
  B.add(chamferCyl(0.0022, 0.0026, 0.0004, 12), 'brass', 'sights',
    place(0, RIB + 0.0012, -0.6440), { wear: 0.3 });

  // magazine tube + end cap
  B.add(tubeZ(0.0118, 0.0098, 0.3800, 0.0008, 20), 'bare', 'receiver',
    place(0, -0.0250, -0.3850), { wear: 0.25 });
  B.add(chamferCyl(0.0128, 0.0130, 0.0009, 20).rotateX(Math.PI / 2), 'bare', 'receiver',
    place(0, -0.0250, -0.5800), { edgeWear: 0.8 });
  B.add(knurl(0.0128, 0.0130, 18, 0.0011), 'bare', 'receiver',
    place(0, -0.0250, -0.5800, Math.PI / 2, 0, 0), { wear: 0.5 });
  // barrel/magazine clamp
  B.add(bevelBox(0.0260, 0.0430, 0.0180, 0.0010), 'steel', 'receiver',
    place(0, -0.0120, -0.5600), { edgeWear: 0.6 });

  /* ---- pump forend ---- */
  const pz = -0.3700;
  B.add(tubeZ(0.0262, 0.0140, 0.1450, 0.0013, 22), 'polymer_fde', 'pump',
    place(0, -0.0130, pz), { edgeWear: 0.30 });
  for (let i = 0; i < 9; i++) {
    B.add(tubeZ(0.0278, 0.0250, 0.0060, 0.0007, 22), 'polymer_fde', 'pump',
      place(0, -0.0130, pz - 0.0560 + i * 0.0140), { edgeWear: 0.55 });
  }
  B.add(tubeZ(0.0272, 0.0230, 0.0110, 0.0010, 22), 'polymer_fde', 'pump',
    place(0, -0.0130, pz + 0.0700), { edgeWear: 0.6 });
  // action bars
  for (const sx of [-1, 1]) {
    B.add(bevelBox(0.0040, 0.0090, 0.1600, 0.0005), 'steel', 'pump',
      place(sx * 0.0170, -0.0120, pz + 0.1250), { wear: 0.4 });
  }

  /* ---- trigger group ---- */
  B.add(sideProfile([
    [-0.1030, -0.0182], [-0.1030, -0.0300], [-0.0100, -0.0300], [0.0100, -0.0280],
    [0.0100, -0.0182],
  ], 0.0030, 0.0330, 0.0010, 4), 'polymer', 'receiver', null, { edgeWear: 0.4 });
  triggerGroup(B, 'receiver', 'trigger', { z: -0.0420, y: -0.0300, width: 0.0125 });
  // cross-bolt safety
  B.add(cylZ(0.0048, 0.0400, 0.0005, 14), 'bare', 'receiver',
    place(0, -0.0250, -0.0180, 0, Math.PI / 2, 0), { edgeWear: 0.9, wear: 0.4 });

  /* ---- stock with a pistol-grip wrist ---- */
  const st = [];
  st.push(sideProfile([
    [0.0080, -0.0250], [0.0700, -0.0430], [0.1250, -0.0480], [0.2100, -0.0400],
    [0.3320, -0.0330], [0.3400, 0.0060], [0.2400, 0.0210], [0.1200, 0.0230],
    [0.0400, 0.0210], [0.0080, 0.0190],
  ], 0.0130, 0.0430, 0.0016, 5));
  B.add(st, 'polymer_fde', 'stock', null, { edgeWear: 0.28 });
  // grip swell under the wrist
  B.add(sideProfile([
    [0.0400, -0.0300], [0.0950, -0.1150], [0.1350, -0.1150], [0.1250, -0.0330],
  ], 0.0140, 0.0380, 0.0016, 5), 'polymer_fde', 'stock', null, { edgeWear: 0.3 });
  // recoil pad
  B.add(sideProfile([
    [0.3320, -0.0340], [0.3450, -0.0230], [0.3450, 0.0040], [0.3320, 0.0070],
  ], 0.0040, 0.0440, 0.0014, 3), 'rubber', 'stock', null, { edgeWear: 0.25 });
  for (let i = 0; i < 4; i++) {
    B.add(bevelBox(0.0430, 0.0028, 0.0034, 0.0007), 'rubber', 'stock',
      place(0, -0.0240 + i * 0.0090, 0.3430), { wear: 0.2 });
  }
  // stippled panels on the grip
  for (const sx of [-1, 1]) {
    const s = stippleField(0.0260, 0.0520, rng, { pitch: 0.0024, height: 0.00028, jitter: 0.28 });
    s.rotateY(sx * Math.PI / 2);
    B.add(s, 'polymer_fde', 'stock', place(sx * 0.0186, -0.0680, 0.1080, 0, 0, -0.35), {});
  }

  B.add(decal('roll_shotgun', 0.0560, 'left', -0.0212, 0.0010, -0.1150),
    'marks', 'receiver', null, { keepUV: true });
  B.add(decal('proof', 0.0110, 'right', 0.0212, -0.0080, -0.0250),
    'marks', 'receiver', null, { keepUV: true });

  return {
    name: 'shotgun',
    sight: { axis: RIB + 0.0034, z: -0.6440 },
    ironSight: { axis: RIB + 0.0034, z: -0.6440 },
    muzzle: new THREE.Vector3(0, 0, -0.6520),
    ejectPort: new THREE.Vector3(0.0250, 0.0050, -0.0740),
    ejectDir: new THREE.Vector3(0.95, 0.24, 0.20).normalize(),
    anchors: {
      rightGrip: {
        point: new THREE.Vector3(0, -0.0680, 0.1080),
        dir: new THREE.Vector3(0, -Math.cos(0.35), Math.sin(0.35)).normalize(),
        radius: 0.0192,
      },
      leftGrip: { point: new THREE.Vector3(0, -0.0130, -0.3700), dir: new THREE.Vector3(0, 0, 1), radius: 0.0288 },
      rightRadial: [0.56, 0.28, 0.78], rightAlong: -0.030,
      leftRadial: [-0.58, -0.82, 0.0], leftAlong: 0.022,
      leftForearm: [-0.30, -0.74, 0.60],
      trigger: new THREE.Vector3(0, -0.0560, -0.0450),
      magGrab: new THREE.Vector3(0, -0.1200, -0.0800),
    },
  };
}

/* ================================================================== *
 * Semi-auto pistol — Glock 17 class.
 * ================================================================== */

export function buildPistol(B, rng) {
  B.bone('root');
  B.bone('frame', 'root');
  B.bone('slide', 'frame', [0, 0, -0.0600]);
  B.bone('mag', 'frame', [0, -0.0280, -0.0060]);
  B.bone('trigger', 'frame', [0, -0.0250, -0.0230]);
  B.bone('slidestop', 'frame', [-0.0142, -0.0080, -0.0080]);
  B.bone('magrelease', 'frame', [0.0150, -0.0300, -0.0140]);
  B.bone('sights', 'slide');

  const bev = 0.0006;
  const SIGHT = 0.0176;
  const slideZ0 = 0.0225, slideZ1 = -0.1520;

  /* ---- slide ---- */
  const slide = [
    [-0.0128, -0.0125], [0.0128, -0.0125], [0.0128, 0.0100], [0.0092, 0.0142],
    [-0.0092, 0.0142], [-0.0128, 0.0100],
  ];
  const slidePort = [
    [-0.0128, -0.0125], [0.0128, -0.0125], [0.0128, -0.0020], [0.0060, -0.0020],
    [0.0060, 0.0142], [-0.0092, 0.0142], [-0.0128, 0.0100],
  ];
  const sg = (pts, z0, z1) => frontProfile(pts, 0.0020, z1 - z0, bev, 4).translate(0, 0, (z0 + z1) / 2);
  B.add([
    sg(slide, slideZ1, -0.0620),
    sg(slidePort, -0.0620, -0.0180),
    sg(slide, -0.0180, slideZ0),
  ], 'metal', 'slide', place(0, 0, 0.0600), { edgeWear: 0.6 });

  // slide serrations, front and rear
  for (let i = 0; i < 7; i++) {
    B.add(bevelBox(0.0272, 0.0034, 0.0026, 0.0006), 'metal', 'slide',
      place(0, 0.0060, 0.0600 + 0.0130 - i * 0.0055, 0, 0, 0), { edgeWear: 0.9 });
  }
  for (let i = 0; i < 4; i++) {
    B.add(bevelBox(0.0272, 0.0034, 0.0026, 0.0006), 'metal', 'slide',
      place(0, 0.0060, 0.0600 - 0.1200 - i * 0.0055), { edgeWear: 0.9 });
  }
  // extractor + ejection-port lip
  B.add(bevelBox(0.0040, 0.0060, 0.0170, 0.0005), 'bare', 'slide',
    place(0.0112, 0.0600 * 0 + 0.0020, 0.0600 - 0.0500), { edgeWear: 0.95, wear: 0.5 });

  // barrel hood and the recoil-spring guide at the muzzle
  B.add(tubeZ(0.0076, 0.0046, 0.0180, 0.0006, 18), 'bright', 'slide',
    place(0, 0.0005, 0.0600 + slideZ1 + 0.0040), { wear: 0.6 });
  B.add(cylZ(0.0044, 0.0140, 0.0005, 14), 'bare', 'slide',
    place(0, -0.0072, 0.0600 + slideZ1 + 0.0035), { wear: 0.5 });

  // sights: rear notch, front blade with a white dot
  B.add(bevelBox(0.0150, 0.0060, 0.0055, 0.0006), 'steel', 'sights',
    place(0, 0.0170, 0.0600 + 0.0130), { edgeWear: 0.9 });
  B.add(bevelBox(0.0042, 0.0075, 0.0060, 0.0004), 'steel', 'sights',
    place(0, 0.0170, 0.0600 - 0.1435), { edgeWear: 0.9 });
  B.add(chamferCyl(0.0016, 0.0016, 0.0003, 12).rotateX(Math.PI / 2), 'bright', 'sights',
    place(0, 0.0186, 0.0600 - 0.1465), { wear: 0.8 });

  /* ---- polymer frame ---- */
  const frame = [
    [-0.1480, -0.0130], [0.0180, -0.0130], [0.0180, -0.0250], [-0.0060, -0.0270],
    [-0.0420, -0.0250], [-0.0600, -0.0230], [-0.1480, -0.0210],
  ];
  B.add(sideProfile(frame, 0.0028, 0.0248, 0.0010, 4), 'polymer', 'frame', null, { edgeWear: 0.35 });
  // dust cover / accessory rail
  B.add(sideProfile([
    [-0.1470, -0.0215], [-0.0640, -0.0215], [-0.0640, -0.0300], [-0.1470, -0.0300],
  ], 0.0016, 0.0210, 0.0008, 3), 'polymer', 'frame', null, { edgeWear: 0.5 });
  for (let i = 0; i < 2; i++) {
    B.add(bevelBox(0.0212, 0.0040, 0.0055, 0.0006), 'polymer', 'frame',
      place(0, -0.0305, -0.0860 - i * 0.0180), { edgeWear: 0.7 });
  }

  const grip = pistolGrip(B, 'frame', rng, {
    tilt: 0.38, length: 0.1010, y: -0.0230, z: 0.0100, mat: 'polymer',
  });
  triggerGroup(B, 'frame', 'trigger', { z: -0.0230, y: -0.0240, width: 0.0110, mat: 'polymer' });
  // trigger safety blade
  B.add(bevelBox(0.0022, 0.0130, 0.0030, 0.0004), 'polymer', 'trigger',
    place(0, -0.0090, -0.0034), { edgeWear: 0.8 });

  // slide stop and magazine release
  B.add(bevelBox(0.0044, 0.0090, 0.0330, 0.0006), 'steel', 'slidestop',
    place(-0.0142, -0.0080, -0.0080), { edgeWear: 0.9 });
  B.add(bevelBox(0.0060, 0.0130, 0.0090, 0.0007), 'polymer', 'magrelease',
    place(0.0150, -0.0300, -0.0140), { edgeWear: 0.8 });
  // takedown levers
  for (const sx of [-1, 1]) {
    B.add(bevelBox(0.0038, 0.0060, 0.0130, 0.0005), 'steel', 'frame',
      place(sx * 0.0136, -0.0170, -0.0480), { edgeWear: 0.9 });
  }

  /* ---- magazine, riding inside the raked grip ---- */
  const magAt = place(0, -0.0280, -0.0060, -0.38, 0, 0);
  stanagMag(B, 'mag', {
    mat: 'polymer', length: 0.1180, width: 0.0230, depth: 0.0300, curve: 0.06, at: magAt,
  });
  B.add(bevelBox(0.0300, 0.0092, 0.0372, 0.0011), 'polymer', 'mag',
    new THREE.Matrix4().multiplyMatrices(magAt, place(0, -0.1225, -0.0010)), { edgeWear: 0.5 });

  B.add(decal('roll_pistol', 0.0420, 'left', -0.0130, 0.0010, -0.0700),
    'marks', 'slide', null, { keepUV: true });
  B.add(decal('serial_small', 0.0200, 'right', 0.0126, -0.0180, -0.0500),
    'marks', 'frame', null, { keepUV: true });

  const gripAxis = new THREE.Vector3(0, -1, 0).transformDirection(grip.matrix).normalize();
  const gripCentre = new THREE.Vector3(0, -0.048, 0).applyMatrix4(grip.matrix);
  return {
    name: 'pistol',
    sight: { axis: SIGHT + 0.0022, z: -0.1465 },
    ironSight: { axis: SIGHT + 0.0022, z: -0.1465 },
    muzzle: new THREE.Vector3(0, 0, -0.1560),
    ejectPort: new THREE.Vector3(0.0140, 0.0060, -0.0400),
    ejectDir: new THREE.Vector3(0.90, 0.42, 0.12).normalize(),
    magBone: 'mag',
    anchors: {
      rightGrip: { point: gripCentre, dir: gripAxis, radius: 0.0180 },
      // support hand wraps the firing hand: a bigger cylinder, offset left
      leftGrip: {
        point: gripCentre.clone().add(new THREE.Vector3(-0.004, 0.004, 0)),
        dir: gripAxis.clone(), radius: 0.0345,
      },
      rightRadial: [0.60, 0.26, 0.76], rightAlong: -0.026,
      leftRadial: [-0.80, 0.10, 0.59], leftAlong: -0.010,
      // The support hand here wraps the FIRING hand, not a handguard tube, so
      // the cylinder axis runs down the grip rather than down the bore. Laying
      // the thumb along it (the C-clamp the long guns get) would drive it up
      // through the slide. Keep the wrapped thumb on the pistol.
      leftThumbAxial: 0,
      leftForearm: [-0.42, -0.44, 0.79],
      trigger: new THREE.Vector3(0, -0.0420, -0.0250),
      magGrab: new THREE.Vector3(0, -0.1250, -0.0060),
    },
    grip,
  };
}
